import {
  ApiError,
  formatDuration,
  queryKeys,
  type TimeClockStatus,
  type TimeEntry,
} from '@mevaser/shared'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, LogOut } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

/**
 * The signed-in staff member's own clock in/out control (t1106), shown in the
 * header for anyone holding `timeclock:punch`. The server stamps the time, so
 * this only ever says "now" — it never sends one.
 */
export function TimeClockButton() {
  const queryClient = useQueryClient()
  const toast = useToast()

  const { data } = useQuery({
    queryKey: queryKeys.timeClock.status(),
    queryFn: () => api.get<TimeClockStatus>('/time-clock/me'),
    refetchInterval: 60_000,
  })
  const open = data?.openEntry ?? null

  // Re-render every half minute while clocked in so the elapsed time moves.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!open) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [open])

  const settle = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.timeClock.all() })
  }
  const fail = (title: string) => async (error: unknown) => {
    toast.error(title, error instanceof ApiError ? error.message : undefined)
    // A 409 means another tab already changed it; reload the truth.
    await settle()
  }

  const punchIn = useMutation({
    mutationFn: () => api.post<TimeEntry>('/time-clock/punch-in', {}),
    onSuccess: async () => {
      await settle()
      toast.success('Clocked in')
    },
    onError: fail('Could not clock in'),
  })

  const punchOut = useMutation({
    mutationFn: () => api.post<TimeEntry>('/time-clock/punch-out', {}),
    onSuccess: async (entry) => {
      await settle()
      toast.success(
        'Clocked out',
        entry.durationMs != null ? formatDuration(entry.durationMs) : undefined,
      )
    },
    onError: fail('Could not clock out'),
  })

  if (!open) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={() => punchIn.mutate()}
        loading={punchIn.isPending}
        aria-label="Clock in"
      >
        <Clock />
        <span className="hidden sm:inline">Clock in</span>
      </Button>
    )
  }

  const since = new Date(open.clockInAt)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" aria-label="Clocked in — open menu">
          <span className="relative flex size-2" aria-hidden="true">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-success" />
          </span>
          <span className="tabular-nums">{formatDuration(now - since.getTime())}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuLabel>
          Clocked in since {since.toLocaleTimeString('en-US', { timeStyle: 'short' })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => punchOut.mutate()} disabled={punchOut.isPending}>
          <LogOut /> Clock out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
